// po.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/** Mirrors backend setting po.vendorSkuMappingMode. */
export type VendorSkuMappingMode = 'STRICT' | 'PROMPT' | 'REQUIRED';

export interface VendorSkuMappingVendorRef {
  id: string;
  name: string;
  /** The vendor on the product master. */
  isPrimary: boolean;
}

/** One PRODUCT on the order; its variants are collapsed into one row. */
export interface VendorSkuMappingItem {
  productId: string;
  sku: string;
  name: string;
  productVariantIds: string[];
  /** Already supplied by the PO vendor (primary vendor or active mapping row). */
  mapped: boolean;
  /** Every vendor that supplies the SKU today, primary first. */
  vendors: VendorSkuMappingVendorRef[];
}

export interface VendorSkuMappingPreview {
  mode: VendorSkuMappingMode;
  vendor: { id: string; name: string };
  items: VendorSkuMappingItem[];
  total: number;
  unmappedCount: number;
}

/** List-page filters shared by the PO and GRN lists (dates are YYYY-MM-DD). */
export interface PurchaseListFilters {
  vendorId?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  status?: string | null;
}

export function appendPurchaseListFilters(
  params: HttpParams,
  filters?: PurchaseListFilters,
): HttpParams {
  if (!filters) return params;
  if (filters.vendorId) params = params.set('vendorId', filters.vendorId);
  if (filters.fromDate) params = params.set('fromDate', filters.fromDate);
  if (filters.toDate) params = params.set('toDate', filters.toDate);
  if (filters.status) params = params.set('status', filters.status);
  return params;
}

@Injectable({
  providedIn: 'root',
})
export class PoService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getNextPoNumber(storeId?: string) {
    let params = new HttpParams();
    if (storeId) params = params.set('storeId', storeId);
    return this.http.get(`${this.baseUrl}/purchase-order/next-number`, {
      params,
    });
  }

  createPo(poData: any) {
    return this.http.post(`${this.baseUrl}/purchase-order`, poData);
  }

  /**
   * Vendor ↔ SKU mapping status of the given lines against a vendor, plus the
   * client's po.vendorSkuMappingMode. Read-only (the URL contains "preview",
   * so the idempotency interceptor leaves it alone).
   */
  previewVendorSkuMapping(
    vendorId: string,
    productVariantIds: string[],
  ): Observable<VendorSkuMappingPreview> {
    return this.http.post<VendorSkuMappingPreview>(
      `${this.baseUrl}/purchase-order/vendor-mapping/preview`,
      { vendorId, productVariantIds },
    );
  }

  /** "Map now": add the vendor as a supplier of these products right away. */
  applyVendorSkuMapping(vendorId: string, productIds: string[]) {
    return this.http.post<{
      mode: VendorSkuMappingMode;
      vendor: { id: string; name: string };
      mapped: Array<{ productId: string; sku: string; name: string }>;
    }>(`${this.baseUrl}/purchase-order/vendor-mapping/apply`, {
      vendorId,
      productIds,
    });
  }

  getPoById(id: string) {
    return this.http.get(`${this.baseUrl}/purchase-order/${id}`);
  }
  getManufacturers() {
    const params = new HttpParams().set('page', 1).set('limit', 1000);
    return this.http.get(`${this.baseUrl}/manufacturers`, { params });
  }

  updatePo(id: string, poData: any) {
    return this.http.patch(`${this.baseUrl}/purchase-order/${id}`, poData);
  }

  getVendors() {
    const params = new HttpParams().set('page', 1).set('limit', 1000);
    return this.http.get(`${this.baseUrl}/vendors`, { params });
  }

  getManufacturerDetails(id: string) {
    return this.http.get(`${this.baseUrl}/manufacturers/${id}`);
  }

  getDepartments() {
    return this.http.get(`${this.baseUrl}/departments`);
  }

  getCategoriesByDepartment(departmentId: string) {
    return this.http.get(
      `${this.baseUrl}/departments/${departmentId}/categories?isActive=true`
    );
  }

  getSubCategoriesByCategory(categoryId: string) {
    return this.http.get(
      `${this.baseUrl}/product-categories/${categoryId}/sub-categories?isActive=true`
    );
  }

  getFilteredProducts(filters: any) {
    let params = new HttpParams({ fromObject: filters });

    // // Add the 'context' parameter
    // params = params.set('context', 'PO');

    return this.http.get(`${this.baseUrl}/products/filter`, {
      params: params,
    });
  }

  getIndentWiseFilteredProducts(filters: any) {
    return this.http.get(`${this.baseUrl}/products/filter/pending-indents`, {
      params: filters,
    });
  }

  searchProducts(query: string, vendorId?: string, locationId?: string) {
    const context = 'GRN';

    let params = new HttpParams().set('query', query).set('context', context);
    if (vendorId) {
      params = params.set('vendorId', vendorId);
    }
    // Receiving location — a Store-type location restricts results to DSD
    // products (enforced server-side).
    if (locationId) {
      params = params.set('locationId', locationId);
    }

    return this.http.get(`${this.baseUrl}/products/search`, { params });
  }

  downloadPurchaseOrder(poId: string) {
    return this.http.get(`${this.baseUrl}/purchase-order/download/${poId}`, {
      responseType: 'blob' as 'blob',
      observe: 'response' as 'response',
    });
  }

  getPurchaseOrders(
    pageOptions: { page: number; limit: number },
    searchText?: string,
    isExpired?: boolean,
    expiredWithoutGrn?: boolean,
    filters?: PurchaseListFilters,
  ) {
    let params = new HttpParams()
      .set('page', pageOptions.page)
      .set('limit', pageOptions.limit);

    if (searchText) {
      params = params.set('search', searchText);
    }
    if (isExpired !== undefined) {
      params = params.set('isExpired', isExpired.toString());
    }
    if (expiredWithoutGrn !== undefined) {
      params = params.set('expiredWithoutGrn', expiredWithoutGrn.toString());
    }
    params = appendPurchaseListFilters(params, filters);
    return this.http.get(`${this.baseUrl}/purchase-order`, { params });
  }

  searchPo(
    poNumber: string,
    isExpired?: boolean,
    expiredWithoutGrn?: boolean,
    filters?: PurchaseListFilters,
  ) {
    let params = new HttpParams().set('poNumber', poNumber);

    if (isExpired !== undefined) {
      params = params.set('isExpired', isExpired.toString());
    }
    if (expiredWithoutGrn !== undefined) {
      params = params.set('expiredWithoutGrn', expiredWithoutGrn.toString());
    }
    params = appendPurchaseListFilters(params, filters);

    return this.http.get(`${this.baseUrl}/purchase-order/search-by-po`, {
      params: params,
    });
  }

  getPurchaseOrdersWithoutGrn() {
    return this.http.get(`${this.baseUrl}/purchase-order/without-grn`);
  }
  generatePoFromIndents(warehouseStoreId: string, indentType: string) {
    const url = `${this.baseUrl}/purchase-order/actions/generate-from-indents`;

    // The chosen warehouse's storeId scopes the generated POs (validated
    // against the caller's access server-side). Empty → backend falls back to
    // the active-store header / legacy derivation.
    const payload = warehouseStoreId ? { storeId: warehouseStoreId } : {};

    return this.http.post(url, payload);
  }

  getPendingItems(poId: string) {
    return this.http.get(
      `${this.baseUrl}/purchase-order/${poId}/pending-items`
    );
  }

  // Close a partially-received PO so it stops appearing in the GRN/GIN
  // dropdowns (user confirmed no more deliveries are expected).
  closePo(poId: string) {
    return this.http.patch(`${this.baseUrl}/purchase-order/${poId}/close`, {});
  }

  /** Delete a DRAFT PO; the backend refuses any other status. */
  deleteDraftPo(poId: string) {
    return this.http.delete(`${this.baseUrl}/purchase-order/${poId}`);
  }

  reopenPo(poId: string) {
    return this.http.patch(`${this.baseUrl}/purchase-order/${poId}/reopen`, {});
  }

  sendEmail(poId: string) {
    return this.http.post(
      `${this.baseUrl}/purchase-order/${poId}/send-email`,
      {}
    );
  }

  downloadTemplate(vendorId?: string) {
    const params = vendorId ? new HttpParams().set('vendorId', vendorId) : new HttpParams();
    return this.http.get(`${this.baseUrl}/purchase-order/template`, {
      responseType: 'blob',
      params,
    });
  }

  parseUpload(file: File, vendorId: string) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(
      `${this.baseUrl}/purchase-order/parse-upload?vendorId=${encodeURIComponent(vendorId)}`,
      formData,
    );
  }

  getUploadUrl(): string {
    return `${this.baseUrl}/purchase-order/upload/purchase-order`;
  }
}
